/**
 * Generated by orval v8.21.0 🍺
 * Do not edit manually.
 * FINRA Market Data API
 * Market data aggregation backend for FINRA data sources
 * OpenAPI spec version: 0.1.0
 */
import {
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  DetailNotFoundErrorResponse,
  GetSearchResponseParams,
  GetTopResultParams,
  HTTPValidationError,
  InternalErrorResponse,
  InvalidRequestErrorResponse,
  SearchResponse,
  TopResult,
  UpstreamAuthenticationFailedErrorResponse,
  UpstreamBadGatewayErrorResponse,
  UpstreamUnavailableErrorResponse
} from '../fINRAMarketDataAPI.schemas.ts';

import { apiFetch } from '../../../lib/api-client.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type getSearchResponseResponse200 = {
  data: SearchResponse
  status: 200
}

export type getSearchResponseResponse400 = {
  data: InvalidRequestErrorResponse
  status: 400
}

export type getSearchResponseResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getSearchResponseResponse500 = {
  data: UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse
  status: 500
}

export type getSearchResponseResponse502 = {
  data: UpstreamBadGatewayErrorResponse
  status: 502
}

export type getSearchResponseResponse503 = {
  data: UpstreamUnavailableErrorResponse
  status: 503
}

export type getSearchResponseResponseSuccess = (getSearchResponseResponse200) & {
  headers: Headers;
};
export type getSearchResponseResponseError = (getSearchResponseResponse400 | getSearchResponseResponse422 | getSearchResponseResponse500 | getSearchResponseResponse502 | getSearchResponseResponse503) & {
  headers: Headers;
};

export type getSearchResponseResponse = (getSearchResponseResponseSuccess | getSearchResponseResponseError)

export const getGetSearchResponseUrl = (params?: GetSearchResponseParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["equity_sector_slug","equity_industry_slug","market_cap_categories","fund_category_slug","exchange"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/search/?${stringifiedParams}` : `/api/v1/search/`
}

/**
 * Get search results for equities, ETFs, and mutual funds matching a query string.
 *
 * Runs three parallel Morningstar screener queries — one per investment type.
 * Each section returns up to 50 results and optional next/prev pagination tokens. Pass
 * the next token back in the corresponding `*_next_pagination_token` param to retrieve
 * the following page, or the prev token in `*_prev_pagination_token` to go back one
 * page. Tokens are independent — each section can be paginated separately, and only
 * one direction may be requested per section per call. Changing the sort parameters
 * requires resetting tokens to None, as sort state is encoded in the token.
 *
 * Args:
 *     query: Search query string — matched against ticker (exact) and name (contains).
 *     investment_type: Optional filter — "equity", "etf", or "mutual_fund". When absent
 *                      all three queries run in parallel. Required when sort_by is provided.
 *     sort_by: Short sort key (e.g. "price", "market_cap", "fund_size", "expense_ratio",
 *              "return_5y", "return_1y"). Requires investment_type. Valid keys depend on
 *              investment type — equity supports price/return_5y/return_1y/market_cap;
 *              etf supports price/return_5y/return_1y/expense_ratio; mutual_fund supports
 *              price/return_5y/return_1y/expense_ratio/fund_size.
 *     order_by: Sort direction — "asc" or "desc". Defaults to "desc".
 *     equity_sector_slug: Optional sector slugs (e.g. "technology", "healthcare") to filter
 *                         equity results. Multiple values are OR'd together. Not valid when
 *                         investment_type is "etf" or "mutual_fund". Ignored when
 *                         equity_industry_slug resolves to at least one valid code —
 *                         industry is the more granular filter and its codes already imply
 *                         a parent sector.
 *     equity_industry_slug: Optional industry slugs (e.g. "biotechnology",
 *                           "consumer-electronics") to filter equity results by industry —
 *                           more granular than equity_sector_slug. Multiple values are OR'd
 *                           together. Takes precedence over equity_sector_slug when given.
 *                           Not valid when investment_type is "etf" or "mutual_fund".
 *     market_cap_categories: Optional list of market cap category slugs (e.g.
 *                            ["large-cap", "mid-cap"]) to filter equity results by market
 *                            cap range. Multiple categories are OR'd together. Valid slugs:
 *                            mega-cap, large-cap, mid-cap, small-cap, micro-cap. Not valid
 *                            when investment_type is "etf" or "mutual_fund".
 *     fund_category_slug: Optional slugs to filter fund results by Morningstar category. Covers
 *                         sector categories (e.g. "technology") and cap-size categories (e.g.
 *                         "large-cap-blend") — both live in the same category field, so any mix
 *                         of sector and cap-size slugs is OR'd together. Not valid for equities.
 *     equity_next_pagination_token: Token from a previous equity page's next_pagination_token,
 *                                    to fetch the following equity page.
 *     equity_prev_pagination_token: Token from a previous equity page's prev_pagination_token,
 *                                   to fetch the prior equity page. Mutually exclusive with
 *                                   equity_next_pagination_token.
 *     etf_next_pagination_token: Token from a previous ETF page's next_pagination_token,
 *                                 to fetch the following ETF page.
 *     etf_prev_pagination_token: Token from a previous ETF page's prev_pagination_token,
 *                                 to fetch the prior ETF page. Mutually exclusive with
 *                                 etf_next_pagination_token.
 *     mutual_fund_next_pagination_token: Token from a previous mutual fund page's
 *                                         next_pagination_token, to fetch the following page.
 *     mutual_fund_prev_pagination_token: Token from a previous mutual fund page's
 *                                         prev_pagination_token, to fetch the prior page.
 *                                         Mutually exclusive with
 *                                         mutual_fund_next_pagination_token.
 *     listing_type: Optional filter — "all", "listed", or "otc". Restricts equity and ETF
 *                   results to that subset of entitled US exchanges. Defaults to "all".
 *                   Not valid when investment_type is "mutual_fund" — mutual funds aren't
 *                   exchange-listed.
 *     exchange: Optional list of individual exchange codes (e.g. ["NYSE", "NAS"]) to
 *               restrict equity and ETF results to. exchange is the child of listing_type —
 *               when both are given, listing_type is ignored and exchange alone determines
 *               the result (e.g. listing_type=otc&exchange=NYSE searches NYSE only). Not
 *               valid when investment_type is "mutual_fund".
 *     morningstar: Injected MorningstarClient instance.
 *
 * Returns:
 *     SearchResponse with separate EquitySearchTable, ETFSearchTable,
 *     and MutualFundSearchTable, each with results and next/prev pagination tokens.
 *
 * Raises:
 *     400: sort_by provided without investment_type, or sort_by not valid for the investment type;
 *          equity_sector_slug or equity_industry_slug provided with a fund investment_type;
 *          market_cap_categories
 *          provided with a fund investment_type, or containing an invalid slug;
 *          fund_category_slug provided with investment_type=equity; listing_type not a valid
 *          option, or provided with investment_type=mutual_fund; exchange containing an
 *          invalid code, or provided with investment_type=mutual_fund; both a next and prev
 *          pagination token provided for the same investment type
 *     500: Morningstar API error or authentication failure
 * @summary Get Search Results
 */
export const getSearchResponse = async (params?: GetSearchResponseParams, options?: RequestInit): Promise<getSearchResponseResponse> => {

  return apiFetch<getSearchResponseResponse>(getGetSearchResponseUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSearchResponseQueryKey = (params?: GetSearchResponseParams,) => {
    return [
    `/api/v1/search/`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetSearchResponseQueryOptions = <TData = Awaited<ReturnType<typeof getSearchResponse>>, TError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(params?: GetSearchResponseParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSearchResponseQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSearchResponse>>> = ({ signal }) => getSearchResponse(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSearchResponseQueryResult = NonNullable<Awaited<ReturnType<typeof getSearchResponse>>>
export type GetSearchResponseQueryError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse


export function useGetSearchResponse<TData = Awaited<ReturnType<typeof getSearchResponse>>, TError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params: undefined |  GetSearchResponseParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSearchResponse>>,
          TError,
          Awaited<ReturnType<typeof getSearchResponse>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSearchResponse<TData = Awaited<ReturnType<typeof getSearchResponse>>, TError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params?: GetSearchResponseParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSearchResponse>>,
          TError,
          Awaited<ReturnType<typeof getSearchResponse>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSearchResponse<TData = Awaited<ReturnType<typeof getSearchResponse>>, TError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params?: GetSearchResponseParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Search Results
 */

export function useGetSearchResponse<TData = Awaited<ReturnType<typeof getSearchResponse>>, TError = InvalidRequestErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params?: GetSearchResponseParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSearchResponse>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSearchResponseQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export type getTopResultResponse200 = {
  data: TopResult
  status: 200
}

export type getTopResultResponse404 = {
  data: DetailNotFoundErrorResponse
  status: 404
}

export type getTopResultResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTopResultResponse500 = {
  data: UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse
  status: 500
}

export type getTopResultResponse502 = {
  data: UpstreamBadGatewayErrorResponse
  status: 502
}

export type getTopResultResponse503 = {
  data: UpstreamUnavailableErrorResponse
  status: 503
}

export type getTopResultResponseSuccess = (getTopResultResponse200) & {
  headers: Headers;
};
export type getTopResultResponseError = (getTopResultResponse404 | getTopResultResponse422 | getTopResultResponse500 | getTopResultResponse502 | getTopResultResponse503) & {
  headers: Headers;
};

export type getTopResultResponse = (getTopResultResponseSuccess | getTopResultResponseError)

export const getGetTopResultUrl = (params: GetTopResultParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/search/top-result?${stringifiedParams}` : `/api/v1/search/top-result`
}

/**
 * Get the Top Result card data for a single ticker, lazy-loaded after the search
 * endpoint identifies it as the top result via `SearchResponse.top_result_identifier`.
 *
 * Args:
 *     ticker: The trading symbol identified as the top result (e.g., "AAPL").
 *     investment_type: The investment type identified for this ticker — "equity",
 *                      "etf", or "mutual_fund".
 *     morningstar: Injected MorningstarClient instance.
 *     morningstar_market_data: Injected MorningstarMarketDataClient instance.
 *
 * Returns:
 *     TopResult with live price, an overview blurb, sector/category info, and a
 *     static related-article blurb for the given investment type.
 *
 * Raises:
 *     400: investment_type is not one of "equity", "etf", "mutual_fund"
 *     404: ticker not found in Morningstar for the given investment_type
 *     500: Morningstar API error or authentication failure
 * @summary Get Top Result
 */
export const getTopResult = async (params: GetTopResultParams, options?: RequestInit): Promise<getTopResultResponse> => {

  return apiFetch<getTopResultResponse>(getGetTopResultUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTopResultQueryKey = (params?: GetTopResultParams,) => {
    return [
    `/api/v1/search/top-result`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetTopResultQueryOptions = <TData = Awaited<ReturnType<typeof getTopResult>>, TError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(params: GetTopResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTopResultQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTopResult>>> = ({ signal }) => getTopResult(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTopResultQueryResult = NonNullable<Awaited<ReturnType<typeof getTopResult>>>
export type GetTopResultQueryError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse


export function useGetTopResult<TData = Awaited<ReturnType<typeof getTopResult>>, TError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params: GetTopResultParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTopResult>>,
          TError,
          Awaited<ReturnType<typeof getTopResult>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTopResult<TData = Awaited<ReturnType<typeof getTopResult>>, TError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params: GetTopResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTopResult>>,
          TError,
          Awaited<ReturnType<typeof getTopResult>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTopResult<TData = Awaited<ReturnType<typeof getTopResult>>, TError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params: GetTopResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Top Result
 */

export function useGetTopResult<TData = Awaited<ReturnType<typeof getTopResult>>, TError = DetailNotFoundErrorResponse | HTTPValidationError | UpstreamAuthenticationFailedErrorResponse | InternalErrorResponse | UpstreamBadGatewayErrorResponse | UpstreamUnavailableErrorResponse>(
 params: GetTopResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopResult>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTopResultQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






